import { ReactNode, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronRight, Menu, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
}

export interface NavSection {
  /** Section heading. `null` hides the heading (use for the first group on lean shells). */
  label: string | null;
  items: NavItem[];
}

/** Route prefixes that render full-bleed (no padding / max-width). */
const FULL_BLEED_PREFIXES = ['/admin/discovery'];

interface ShellProps {
  /** Brand line shown at the top of the dark sidebar. */
  brand: ReactNode;
  /** Grouped nav. A single-section nav can pass one section with `label: null`. */
  sections: NavSection[];
  /** Right-aligned header content (UserButton, OrgSwitcher, etc). */
  headerSlot: ReactNode;
  /** Optional small label above the content header (e.g. user role). */
  scopeLabel?: string;
}

const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Two-zone app shell. At lg+ the dark sidebar is an in-flow left column.
 * Below lg it collapses to an overlay drawer triggered by a hamburger in
 * the header — closes on route change, Escape, scrim click. Aside is
 * `inert` when closed at mobile so screen readers and tab focus skip it.
 */
export function Shell({ brand, sections, headerSlot, scopeLabel }: ShellProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const location = useLocation();
  const fullBleed = FULL_BLEED_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsedSections);
  const toggleSection = (label: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      writeCollapsedSections(next);
      return next;
    });
  };

  // Auto-close drawer when the user navigates.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer + restores focus to the trigger.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Lock body scroll while the mobile drawer is open. No-op at lg+.
  useEffect(() => {
    if (!drawerOpen || isDesktop) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, isDesktop]);

  const drawerHidden = !isDesktop && !drawerOpen;

  return (
    <div className="flex min-h-screen text-foreground">
      {/* Scrim. Only at <lg when drawer is open. */}
      {drawerOpen && !isDesktop && (
        <div
          onClick={() => setDrawerOpen(false)}
          aria-hidden
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <aside
        id="primary-nav"
        aria-label="Main navigation"
        aria-hidden={drawerHidden || undefined}
        inert={drawerHidden}
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen w-60 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
          'transition-transform duration-200 ease-out-quart motion-reduce:transition-none',
          'lg:sticky lg:shrink-0 lg:translate-x-0 lg:transition-none',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between gap-2 px-4">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-lg px-1 py-1"
          >
            {brand}
          </Link>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="-mr-2 rounded-md p-1.5 text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground lg:hidden"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-5 pt-2">
          {sections.map((section, idx) => {
            const listId = `nav-section-${idx}`;
            const containsActive = section.items.some((item) => isNavActive(location.pathname, item));
            // The section holding the current page always stays open.
            const isCollapsed = section.label !== null && !containsActive && collapsed.has(section.label);
            const hiddenBadges = section.items.reduce((n, item) => n + (item.badge ?? 0), 0);
            return (
              <div key={section.label ?? `section-${idx}`} className={idx > 0 ? 'mt-4' : ''}>
                {section.label !== null && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.label as string)}
                    aria-expanded={!isCollapsed}
                    aria-controls={listId}
                    disabled={containsActive}
                    title={containsActive ? 'Holds the page you are on' : undefined}
                    className="group/sec flex w-full items-center gap-1.5 rounded-md px-2.5 pb-1.5 pt-0.5 text-left text-[0.6875rem] font-semibold tracking-[0.02em] text-sidebar-muted-foreground/80 transition-colors hover:text-sidebar-foreground disabled:cursor-default disabled:hover:text-sidebar-muted-foreground/80"
                  >
                    <ChevronRight
                      aria-hidden
                      className={cn(
                        'h-3 w-3 shrink-0 transition-transform duration-150 ease-out-quart motion-reduce:transition-none',
                        !isCollapsed && 'rotate-90',
                        containsActive && 'opacity-40',
                      )}
                      strokeWidth={2.25}
                    />
                    <span className="flex-1">{section.label}</span>
                    {isCollapsed && hiddenBadges > 0 && (
                      <span className="flex h-[1.125rem] min-w-[1.25rem] items-center justify-center rounded-full bg-brand/15 px-1.5 font-mono text-[10px] font-medium leading-none text-sidebar-active">
                        {hiddenBadges > 99 ? '99+' : hiddenBadges}
                      </span>
                    )}
                  </button>
                )}
                <ul id={listId} hidden={isCollapsed} className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-[0.4375rem] text-sm font-medium transition-colors duration-150 ease-out-quart',
                            isActive
                              ? 'bg-sidebar-accent font-semibold text-sidebar-foreground shadow-[var(--highlight)]'
                              : 'text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              className={cn('h-4 w-4 shrink-0', isActive && 'text-sidebar-active')}
                              strokeWidth={1.85}
                            />
                            <span className="flex-1">{item.label}</span>
                            {item.badge != null && item.badge > 0 && (
                              <span className="ml-auto flex h-[1.125rem] min-w-[1.25rem] items-center justify-center rounded-full bg-brand/15 px-1.5 font-mono text-[10px] font-medium leading-none text-sidebar-active">
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="app-header sticky top-0 z-10 flex h-14 items-center justify-between gap-4 border-b border-border px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="-ml-1 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              aria-controls="primary-nav"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            {scopeLabel ? (
              <p className="text-[0.8125rem] font-semibold text-muted-foreground">
                {scopeLabel}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">{headerSlot}</div>
        </header>
        <main
          className={cn(
            'min-w-0 flex-1',
            fullBleed ? 'overflow-hidden' : 'px-4 pb-12 pt-6 lg:px-8',
          )}
        >
          {fullBleed ? (
            <Outlet />
          ) : (
            <div className="mx-auto w-full max-w-[1280px]">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * SSR-safe media query subscription via useSyncExternalStore. Returns
 * `false` during the first render on the server / before hydration —
 * acceptable here because the drawer state simply defaults to "mobile
 * collapsed," which is the safe initial paint regardless of viewport.
 */
function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      if (typeof window === 'undefined') return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', notify);
      return () => mql.removeEventListener('change', notify);
    },
    () => {
      if (typeof window === 'undefined') return false;
      return window.matchMedia(query).matches;
    },
    () => false,
  );
}

const COLLAPSED_KEY = 'spade-nav-collapsed';

/** Sidebar groups the person has folded, remembered per browser. */
function readCollapsedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeCollapsedSections(labels: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...labels]));
  } catch {
    // Storage blocked: the fold still applies for this visit.
  }
}

/** Same matching NavLink uses: exact for `end` items, prefix otherwise. */
export function isNavActive(pathname: string, item: Pick<NavItem, 'to' | 'end'>): boolean {
  if (item.end) return pathname === item.to || pathname === `${item.to}/`;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
